/*
 * Positional astronomy for StarGuessr: where a star, the Sun, or the Moon sits
 * in the sky for a given place and instant.
 *
 * Conventions used throughout:
 *   - Angles are degrees at every boundary; radians only appear inside a function.
 *   - Longitude is east-positive, latitude north-positive.
 *   - Azimuth is measured from true north, increasing toward east (N=0, E=90).
 *   - Altitude is degrees above the horizon; negative means below it.
 *
 * Precession, nutation, aberration and refraction are all ignored. The game only
 * needs the sky to be internally consistent and recognizable, and the largest of
 * those (precession, ~1.4 deg/century) never shows up because rounds are always
 * dated within a year or so of now.
 */

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

const sinD = (deg) => Math.sin(deg * DEG);
const cosD = (deg) => Math.cos(deg * DEG);

export function normalizeDeg(deg) {
  const wrapped = deg % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

// Signed difference a - b, folded into [-180, 180].
export function angleDeltaDeg(a, b) {
  const diff = normalizeDeg(a - b);
  return diff > 180 ? diff - 360 : diff;
}

// The Unix epoch (1970-01-01T00:00:00Z) is JD 2440587.5.
export function julianDate(date) {
  return date.getTime() / 86400000 + 2440587.5;
}

const J2000 = 2451545.0;

/*
 * Greenwich mean sidereal time, via the IAU/Meeus polynomial. The 360.9856...
 * coefficient is the sidereal day: the sky turns ~1.0027x faster than the clock,
 * which is the whole reason the same star rises about 4 minutes earlier each
 * night, and in this game the reason a fixed clock time still tells you the date.
 */
export function gmstDegrees(jd) {
  const d = jd - J2000;
  const t = d / 36525;
  const gmst =
    280.46061837 +
    360.98564736629 * d +
    0.000387933 * t * t -
    (t * t * t) / 38710000;
  return normalizeDeg(gmst);
}

export function lstDegrees(jd, lonDeg) {
  return normalizeDeg(gmstDegrees(jd) + lonDeg);
}

/*
 * Equatorial (RA/Dec) to horizontal (alt/az) for an observer.
 *
 * Done as an explicit basis projection rather than the usual pair of
 * sin/atan2 identities: the identity form has a well-known quadrant trap in
 * azimuth, and this version is the same arithmetic with the ambiguity removed.
 */
export function equatorialToHorizontal(raDeg, decDeg, latDeg, lstDeg) {
  const ha = lstDeg - raDeg;

  const north = -cosD(decDeg) * cosD(ha) * sinD(latDeg) + sinD(decDeg) * cosD(latDeg);
  const east = -cosD(decDeg) * sinD(ha);
  const up = cosD(decDeg) * cosD(ha) * cosD(latDeg) + sinD(decDeg) * sinD(latDeg);

  return {
    alt: Math.asin(Math.max(-1, Math.min(1, up))) * RAD,
    az: normalizeDeg(Math.atan2(east, north) * RAD)
  };
}

/*
 * A direction in the observer's sky, as a unit vector in equatorial space
 * (x toward RA 0/Dec 0, y toward RA 90/Dec 0, z toward the north celestial pole).
 *
 * The sky viewer needs this to paint the Milky Way: rather than converting every
 * screen pixel back to RA/Dec through the full trig, it transforms the three
 * camera basis vectors once per frame and takes linear combinations per pixel.
 * Inputs are the horizontal components used above — east, north, up.
 */
export function horizontalVectorToEquatorial(east, north, up, latDeg, lstDeg) {
  const a = up * cosD(latDeg) - north * sinD(latDeg);
  const b = -east;

  return {
    x: cosD(lstDeg) * a + sinD(lstDeg) * b,
    y: sinD(lstDeg) * a - cosD(lstDeg) * b,
    z: up * sinD(latDeg) + north * cosD(latDeg)
  };
}

function eclipticToEquatorial(lonDeg, latDeg, obliquityDeg) {
  const x = cosD(latDeg) * cosD(lonDeg);
  const y = cosD(latDeg) * sinD(lonDeg);
  const z = sinD(latDeg);

  const yq = y * cosD(obliquityDeg) - z * sinD(obliquityDeg);
  const zq = y * sinD(obliquityDeg) + z * cosD(obliquityDeg);

  return {
    ra: normalizeDeg(Math.atan2(yq, x) * RAD),
    dec: Math.asin(Math.max(-1, Math.min(1, zq))) * RAD
  };
}

function obliquityDeg(jd) {
  return 23.439 - 0.0000004 * (jd - J2000);
}

/*
 * Low-precision solar position (Astronomical Almanac's abridged series), good to
 * about 0.01 deg. Ample here: the Sun is only used to decide whether it is dark
 * enough to be night, and to measure the Moon's phase angle against.
 */
export function sunPosition(jd) {
  const n = jd - J2000;
  const meanLongitude = normalizeDeg(280.46 + 0.9856474 * n);
  const meanAnomaly = normalizeDeg(357.528 + 0.9856003 * n);
  const eclipticLongitude = normalizeDeg(
    meanLongitude + 1.915 * sinD(meanAnomaly) + 0.02 * sinD(2 * meanAnomaly)
  );

  const { ra, dec } = eclipticToEquatorial(eclipticLongitude, 0, obliquityDeg(jd));
  return { ra, dec, eclipticLongitude };
}

/*
 * Low-precision lunar position, following Schlyter's abridged form of the
 * classical lunar theory: a Keplerian orbit plus the dozen largest periodic
 * perturbations. Good to a couple of arcminutes, far finer than anyone can read
 * off a screen.
 *
 * The Moon earns this complexity because it is the game's only independent date
 * signal. It laps the star background about once a month, so its phase and its
 * position among the stars are what make the Historic tier's date guessable at
 * all. The stars alone repeat too closely from night to night.
 */
export function moonPosition(jd) {
  const d = jd - 2451543.5;

  const node = normalizeDeg(125.1228 - 0.0529538083 * d);
  const inclination = 5.1454;
  const perigeeArg = normalizeDeg(318.0634 + 0.1643573223 * d);
  const semiMajorAxis = 60.2666;
  const eccentricity = 0.0549;
  const meanAnomaly = normalizeDeg(115.3654 + 13.0649929509 * d);

  const sunMeanAnomaly = normalizeDeg(356.047 + 0.9856002585 * d);
  const sunPerihelionArg = normalizeDeg(282.9404 + 0.0000470935 * d);
  const sunMeanLongitude = normalizeDeg(sunMeanAnomaly + sunPerihelionArg);

  let eccentricAnomaly =
    meanAnomaly + RAD * eccentricity * sinD(meanAnomaly) * (1 + eccentricity * cosD(meanAnomaly));
  for (let i = 0; i < 3; i += 1) {
    const delta =
      (eccentricAnomaly - RAD * eccentricity * sinD(eccentricAnomaly) - meanAnomaly) /
      (1 - eccentricity * cosD(eccentricAnomaly));
    eccentricAnomaly -= delta;
  }

  const xOrbit = semiMajorAxis * (cosD(eccentricAnomaly) - eccentricity);
  const yOrbit = semiMajorAxis * Math.sqrt(1 - eccentricity * eccentricity) * sinD(eccentricAnomaly);
  const distance = Math.hypot(xOrbit, yOrbit);
  const trueAnomaly = normalizeDeg(Math.atan2(yOrbit, xOrbit) * RAD);

  const argLat = trueAnomaly + perigeeArg;
  const xEcl =
    distance * (cosD(node) * cosD(argLat) - sinD(node) * sinD(argLat) * cosD(inclination));
  const yEcl =
    distance * (sinD(node) * cosD(argLat) + cosD(node) * sinD(argLat) * cosD(inclination));
  const zEcl = distance * sinD(argLat) * sinD(inclination);

  let eclipticLongitude = normalizeDeg(Math.atan2(yEcl, xEcl) * RAD);
  let eclipticLatitude = Math.atan2(zEcl, Math.hypot(xEcl, yEcl)) * RAD;

  const moonMeanLongitude = normalizeDeg(node + perigeeArg + meanAnomaly);
  const elongation = normalizeDeg(moonMeanLongitude - sunMeanLongitude);
  const argOfLatitude = normalizeDeg(moonMeanLongitude - node);

  eclipticLongitude +=
    -1.274 * sinD(meanAnomaly - 2 * elongation) +
    0.658 * sinD(2 * elongation) +
    -0.186 * sinD(sunMeanAnomaly) +
    -0.059 * sinD(2 * meanAnomaly - 2 * elongation) +
    -0.057 * sinD(meanAnomaly - 2 * elongation + sunMeanAnomaly) +
    0.053 * sinD(meanAnomaly + 2 * elongation) +
    0.046 * sinD(2 * elongation - sunMeanAnomaly) +
    0.041 * sinD(meanAnomaly - sunMeanAnomaly) +
    -0.035 * sinD(elongation) +
    -0.031 * sinD(meanAnomaly + sunMeanAnomaly) +
    -0.015 * sinD(2 * argOfLatitude - 2 * elongation) +
    0.011 * sinD(meanAnomaly - 4 * elongation);

  eclipticLatitude +=
    -0.173 * sinD(argOfLatitude - 2 * elongation) +
    -0.055 * sinD(meanAnomaly - argOfLatitude - 2 * elongation) +
    -0.046 * sinD(meanAnomaly + argOfLatitude - 2 * elongation) +
    0.033 * sinD(argOfLatitude + 2 * elongation) +
    0.017 * sinD(2 * meanAnomaly + argOfLatitude);

  eclipticLongitude = normalizeDeg(eclipticLongitude);

  const { ra, dec } = eclipticToEquatorial(eclipticLongitude, eclipticLatitude, obliquityDeg(jd));
  return { ra, dec, eclipticLongitude, eclipticLatitude, distance };
}

/*
 * Illuminated fraction and waxing/waning, from the Sun-Moon elongation.
 *
 * Uses geocentric elongation as a stand-in for the true phase angle. They differ
 * by well under a degree because the Sun is ~390x farther away than the Moon,
 * which moves the terminator by less than a pixel at any size we draw it.
 */
export function moonPhase(jd) {
  const sun = sunPosition(jd);
  const moon = moonPosition(jd);
  const elongation = normalizeDeg(moon.eclipticLongitude - sun.eclipticLongitude);

  return {
    illuminated: (1 - cosD(elongation)) / 2,
    waxing: elongation < 180,
    elongation
  };
}

export function sunHorizontal(latDeg, lonDeg, date) {
  const jd = julianDate(date);
  const sun = sunPosition(jd);
  return equatorialToHorizontal(sun.ra, sun.dec, latDeg, lstDegrees(jd, lonDeg));
}

export function sunAltitude(latDeg, lonDeg, date) {
  return sunHorizontal(latDeg, lonDeg, date).alt;
}

// -18 deg is astronomical twilight: the Sun is far enough down that even the
// faintest catalog stars are visible, so every round shows a genuinely full sky.
export const ASTRONOMICAL_TWILIGHT_DEG = -18;

export function isNight(latDeg, lonDeg, date, thresholdDeg = ASTRONOMICAL_TWILIGHT_DEG) {
  return sunAltitude(latDeg, lonDeg, date) < thresholdDeg;
}

/*
 * The naked-eye planets.
 *
 * Same abridged Keplerian approach as the Moon, from Schlyter: each element is
 * linear in days from the 1999-12-31 epoch, solve Kepler for the eccentric
 * anomaly, then rotate the orbital position into ecliptic coordinates. Adding
 * the Sun's geocentric vector turns heliocentric into geocentric, because the
 * Sun's position seen from Earth is exactly Earth's heliocentric position
 * negated.
 *
 * Accurate to a few arcminutes over the game's date range, which is far below
 * what anyone can read off the screen. Long-period perturbations between Jupiter
 * and Saturn are ignored; over a year either side of now they stay small.
 *
 * Planets matter here because they are often the brightest things in the sky and
 * move against the stars. Leaving them out is why a rendered sky can look subtly
 * wrong to anyone who actually looks up: Venus or Jupiter simply missing from an
 * otherwise correct field.
 */
const PLANET_ELEMENTS = [
  { name: 'Mercury', N: [48.3313, 3.24587e-5], i: [7.0047, 5.0e-8], w: [29.1241, 1.01444e-5],
    a: [0.387098, 0], e: [0.205635, 5.59e-10], M: [168.6562, 4.0923344368],
    h0: -0.36, phase: 0.027, phase6: 2.2e-13 },
  { name: 'Venus', N: [76.6799, 2.4659e-5], i: [3.3946, 2.75e-8], w: [54.891, 1.38374e-5],
    a: [0.72333, 0], e: [0.006773, -1.302e-9], M: [48.0052, 1.6021302244],
    h0: -4.34, phase: 0.013, phase3: 4.2e-7 },
  { name: 'Mars', N: [49.5574, 2.11081e-5], i: [1.8497, -1.78e-8], w: [286.5016, 2.92961e-5],
    a: [1.523688, 0], e: [0.093405, 2.516e-9], M: [18.6021, 0.5240207766], h0: -1.51, phase: 0.016 },
  { name: 'Jupiter', N: [100.4542, 2.76854e-5], i: [1.303, -1.557e-7], w: [273.8777, 1.64505e-5],
    a: [5.20256, 0], e: [0.048498, 4.469e-9], M: [19.895, 0.0830853001], h0: -9.25, phase: 0.014 },
  { name: 'Saturn', N: [113.6634, 2.3898e-5], i: [2.4886, -1.081e-7], w: [339.3939, 2.97661e-5],
    a: [9.55475, 0], e: [0.055546, -9.499e-9], M: [316.967, 0.0334442282], h0: -9.0, phase: 0.044 },
  { name: 'Uranus', N: [74.0005, 1.3978e-5], i: [0.7733, 1.9e-8], w: [96.6612, 3.0565e-5],
    a: [19.18171, -1.55e-8], e: [0.047318, 7.45e-9], M: [142.5905, 0.011725806], h0: -7.15, phase: 0.001 }
];

const EARTH = { w: [282.9404, 4.70935e-5], e: [0.016709, -1.151e-9], M: [356.047, 0.9856002585] };

function element(pair, d) {
  return pair[0] + pair[1] * d;
}

// Newton's method on Kepler's equation. Three passes is ample at these
// eccentricities; even Mercury's 0.21 converges well inside a milliarcsecond.
function eccentricAnomaly(meanAnomaly, e) {
  let E = meanAnomaly + RAD * e * sinD(meanAnomaly) * (1 + e * cosD(meanAnomaly));
  for (let i = 0; i < 4; i += 1) {
    E -= (E - RAD * e * sinD(E) - meanAnomaly) / (1 - e * cosD(E));
  }
  return E;
}

// The Sun as seen from Earth, in rectangular ecliptic coordinates.
function sunRectangular(d) {
  const w = element(EARTH.w, d);
  const e = element(EARTH.e, d);
  const M = normalizeDeg(element(EARTH.M, d));
  const E = eccentricAnomaly(M, e);

  const xv = cosD(E) - e;
  const yv = Math.sqrt(1 - e * e) * sinD(E);
  const r = Math.hypot(xv, yv);
  const lon = normalizeDeg(Math.atan2(yv, xv) * RAD + w);

  return { x: r * cosD(lon), y: r * sinD(lon), r };
}

export function planetPositions(jd) {
  const d = jd - 2451543.5;
  const sun = sunRectangular(d);
  const obliquity = obliquityDeg(jd);

  return PLANET_ELEMENTS.map((planet) => {
    const N = element(planet.N, d);
    const i = element(planet.i, d);
    const w = element(planet.w, d);
    const a = element(planet.a, d);
    const e = element(planet.e, d);
    const M = normalizeDeg(element(planet.M, d));

    const E = eccentricAnomaly(M, e);
    const xv = a * (cosD(E) - e);
    const yv = a * Math.sqrt(1 - e * e) * sinD(E);
    const r = Math.hypot(xv, yv);
    const v = Math.atan2(yv, xv) * RAD;
    const u = v + w;

    const xh = r * (cosD(N) * cosD(u) - sinD(N) * sinD(u) * cosD(i));
    const yh = r * (sinD(N) * cosD(u) + cosD(N) * sinD(u) * cosD(i));
    const zh = r * sinD(u) * sinD(i);

    const xg = xh + sun.x;
    const yg = yh + sun.y;
    const distance = Math.sqrt(xg * xg + yg * yg + zh * zh);

    const lon = normalizeDeg(Math.atan2(yg, xg) * RAD);
    const lat = Math.atan2(zh, Math.hypot(xg, yg)) * RAD;
    const { ra, dec } = eclipticToEquatorial(lon, lat, obliquity);

    /*
     * Brightness from the standard absolute-magnitude relation: distance dims it
     * by 5*log10(r*R), plus a phase correction, since a planet showing a crescent
     * is dimmer than the same planet full.
     *
     * Mercury and Venus need the higher-order phase terms. They are the two that
     * pass between us and the Sun, so they reach very large phase angles, and the
     * linear term alone leaves a thin crescent Venus far too bright: it peaked at
     * magnitude -5.7 without the cubic, against a true limit near -4.9.
     *
     * Saturn's rings are not modelled. They swing its brightness by up to about a
     * magnitude over the ring-tilt cycle, so Saturn is the least accurate here.
     */
    const cosPhase = Math.min(1, Math.max(-1, (r * r + distance * distance - sun.r * sun.r) / (2 * r * distance)));
    const phaseAngle = Math.acos(cosPhase) * RAD;
    const magnitude =
      planet.h0 +
      5 * Math.log10(r * distance) +
      planet.phase * phaseAngle +
      (planet.phase3 ? planet.phase3 * phaseAngle ** 3 : 0) +
      (planet.phase6 ? planet.phase6 * phaseAngle ** 6 : 0);

    return { name: planet.name, ra, dec, mag: magnitude, distanceAu: distance };
  });
}
